/**
 * Environment configuration system
 * 
 * Centralizes all environment-specific settings, feature flags, and API endpoints.
 * This provides a single source of truth for environment configuration.
 */

/**
 * Environment types
 */
export type Environment = 'development' | 'production' | 'test';

/**
 * Feature flags configuration
 */
export interface FeatureFlags {
  /** Enable performance monitoring */
  enablePerformanceMonitoring: boolean;
  /** Enable debug logging */
  enableDebugLogging: boolean;
  /** Enable error reporting */
  enableErrorReporting: boolean;
  /** Enable analytics tracking */
  enableAnalytics: boolean;
  /** Enable AI features */
  enableAIFeatures: boolean;
  /** Enable Supabase integration */
  enableSupabase: boolean;
  /** Enable offline mode */
  enableOfflineMode: boolean;
  /** Enable experimental features */
  enableExperimentalFeatures: boolean;
  /** InvoiceManager: integration reconnect + resume (QBO + M365) */
  enableInvoiceIntegrationReconnectV1: boolean;
  /** InvoiceManager: PDF caching (Phase 4) */
  enableInvoicePdfCacheV1: boolean;
}

/**
 * API endpoints configuration
 */
export interface APIEndpoints {
  /** Supabase URL */
  supabaseUrl: string | null;
  /** Supabase anonymous key */
  supabaseAnonKey: string | null;
  /** Gemini API endpoint (if custom) */
  geminiApiEndpoint: string;
  /** OpenAI API endpoint */
  openAIApiEndpoint: string;
  /** Cloudflare Worker URL for PDF generation */
  pdfWorkerUrl: string | null;
  /** Cloudflare Worker URL for AI API proxy */
  aiWorkerUrl: string | null;
  /** Cloudflare Worker URL for D1 storage */
  storageWorkerUrl: string | null;
  /** Cloudflare Worker URL for Cursor API proxy */
  cursorWorkerUrl: string | null;
}

/**
 * Debug configuration
 */
export interface DebugConfig {
  /** Enable verbose logging */
  verbose: boolean;
  /** Log all API calls */
  logApiCalls: boolean;
  /** Log all state changes */
  logStateChanges: boolean;
  /** Log performance metrics */
  logPerformance: boolean;
  /** Show performance monitor in UI */
  showPerformanceMonitor: boolean;
  /** Mock API responses */
  mockApiResponses: boolean;
}

/**
 * Get current environment
 */
export function getEnvironment(): Environment {
  if (typeof window === 'undefined') {
    return 'production';
  }

  // Check for explicit environment variable
  const env = import.meta.env.MODE || import.meta.env.NODE_ENV || 'production';
  
  if (env === 'development' || env === 'dev') {
    return 'development';
  }
  
  if (env === 'test') {
    return 'test';
  }
  
  return 'production';
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string | null = null): string | null {
  try {
    // Vite uses import.meta.env
    const value = import.meta.env[key];
    if (value && value !== 'undefined' && value !== 'null') {
      return value;
    }
  } catch (e) {
    // Ignore
  }
  
  return fallback;
}

function getEnvBoolVar(key: string, fallback: boolean): boolean {
  const value = getEnvVar(key, null);
  if (value == null) return fallback;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

/**
 * Check if running in development
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if running in production
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * Check if running in test
 */
export function isTest(): boolean {
  return getEnvironment() === 'test';
}

/**
 * Feature flags configuration
 */
export const featureFlags: FeatureFlags = {
  enablePerformanceMonitoring: isDevelopment(),
  enableDebugLogging: isDevelopment(),
  enableErrorReporting: isProduction(),
  enableAnalytics: isProduction(),
  enableAIFeatures: true,
  enableSupabase: getEnvVar('VITE_SUPABASE_URL') !== null,
  enableOfflineMode: true,
  enableExperimentalFeatures: isDevelopment(),
  enableInvoiceIntegrationReconnectV1: getEnvBoolVar('VITE_ENABLE_INVOICE_INTEGRATION_RECONNECT_V1', true),
  enableInvoicePdfCacheV1: getEnvBoolVar('VITE_ENABLE_INVOICE_PDF_CACHE_V1', true),
};

/**
 * API endpoints configuration
 */
export const apiEndpoints: APIEndpoints = {
  supabaseUrl: getEnvVar('VITE_SUPABASE_URL', null),
  supabaseAnonKey: getEnvVar('VITE_SUPABASE_ANON_KEY', null),
  geminiApiEndpoint: getEnvVar('VITE_GEMINI_API_ENDPOINT', 'https://generativelanguage.googleapis.com'),
  openAIApiEndpoint: getEnvVar('VITE_OPENAI_API_ENDPOINT', 'https://api.openai.com/v1'),
  pdfWorkerUrl: getEnvVar('VITE_PDF_WORKER_URL', null),
  aiWorkerUrl: getEnvVar('VITE_AI_WORKER_URL', null),
  storageWorkerUrl: getEnvVar('VITE_STORAGE_WORKER_URL', null),
  cursorWorkerUrl: getEnvVar('VITE_CURSOR_WORKER_URL', null),
};

/**
 * Debug configuration
 */
export const debugConfig: DebugConfig = {
  verbose: isDevelopment(),
  logApiCalls: isDevelopment(),
  logStateChanges: isDevelopment() && getEnvVar('VITE_DEBUG_STATE', 'false') === 'true',
  logPerformance: isDevelopment(),
  showPerformanceMonitor: isDevelopment(),
  mockApiResponses: isTest() && getEnvVar('VITE_MOCK_API', 'false') === 'true',
};

/**
 * Get API key for a service
 */
/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(feature: keyof FeatureFlags): boolean {
  return featureFlags[feature];
}

/**
 * Get debug configuration value
 */
export function getDebugConfig(key: keyof DebugConfig): boolean {
  return debugConfig[key];
}

/**
 * Environment configuration object
 */
export const envConfig = {
  environment: getEnvironment(),
  isDevelopment: isDevelopment(),
  isProduction: isProduction(),
  isTest: isTest(),
  featureFlags,
  apiEndpoints,
  debugConfig,
  isFeatureEnabled,
  getDebugConfig,
} as const;

/**
 * Log environment info (development only)
 */
if (isDevelopment()) {
  console.log('[Environment]', {
    environment: getEnvironment(),
    featureFlags,
    apiEndpoints: {
      ...apiEndpoints,
      supabaseUrl: apiEndpoints.supabaseUrl ? '***configured***' : null,
      supabaseAnonKey: apiEndpoints.supabaseAnonKey ? '***configured***' : null,
    },
    debugConfig,
  });
}
